// webapp/src/components/AdminRoute.tsx
//
// Admin route guard — Phase 08 CONTEXT.md D-01..D-03.
// Two-phase check: (1) user authenticated via useAuth(), (2) user is admin via is_admin() RPC.
// SECURITY: adminState initialized to 'loading' — never 'allowed'.
// Initializing to 'allowed' would flash admin content before RPC check completes (UI-SPEC AC-4).
// Redirect: silent Navigate to "/" for both unauthenticated and non-admin users (D-02).
// No Header/Footer — admin layout uses its own topbar + sidebar (D-04).

import { useState, useEffect, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { supabase } from '../lib/supabase'
import { checkIsAdminWithRefresh } from '../lib/adminCheck'

type AdminCheckState = 'loading' | 'allowed' | 'denied'

export default function AdminRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  // CRITICAL: 'loading' initial state prevents any flash of admin content (UI-SPEC AC-4).
  const [adminState, setAdminState] = useState<AdminCheckState>('loading')

  useEffect(() => {
    if (loading || !user) return  // Wait for auth, or unauthenticated handled at render time

    let cancelled = false
    // Auth confirmed + user exists → check admin role via SECURITY DEFINER RPC.
    // is_admin() derives auth.uid() server-side from the JWT — cannot be spoofed by client.
    // Phase 17 IN-08-02 : on stale-JWT errors (PGRST301 or "JWT" in message), the
    // helper attempts ONE supabase.auth.refreshSession() + retries is_admin once
    // before returning 'denied'. Network or other errors short-circuit to denied.
    checkIsAdminWithRefresh(supabase).then((next) => {
      if (cancelled) return
      setAdminState(next)
    })
    return () => {
      cancelled = true
    }
  }, [user, loading])

  // Spinner during auth session hydration (AuthProvider.onAuthStateChange).
  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center" aria-live="polite">
        <p className="text-text-muted text-sm">Chargement…</p>
      </div>
    )
  }

  // Unauthenticated → silent redirect (D-02, ADMIN-01). Render-time check avoids
  // writing 'denied' to state inside the effect (react-hooks/set-state-in-effect).
  if (!user) {
    return <Navigate to="/" replace />
  }

  // Authenticated, RPC in-flight: show spinner (UI-SPEC AC-4 prevents content flash).
  if (adminState === 'loading') {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center" aria-live="polite">
        <p className="text-text-muted text-sm">Chargement…</p>
      </div>
    )
  }

  // Silent redirect for non-admin authenticated user (D-02).
  if (adminState === 'denied') {
    return <Navigate to="/" replace />
  }

  // adminState === 'allowed': is_admin() returned true for this authenticated user.
  return <>{children}</>
}
